import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import useVerifyEmailOtp from '@/services/api/auth/useVerifyEmailOtp'
import { Mail, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import useUpdateEmail from '@/services/api/auth/useUpdateEmail'

export function EmailChangeSection() {
  const { data: userData, status } = useGetCurrentUser()
  const [showOtpDialog, setShowOtpDialog] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [pendingEmail, setPendingEmail] = useState('')

  const emailSchema = z.object({
    email: z
      .string()
      .email('Please enter a valid email address')
      .min(1, 'Email is required'),
  })

  const otpSchema = z.object({
    otp: z.string().length(6, 'OTP must be 6 digits').min(1, 'OTP is required'),
  })

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  })

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: '',
    },
  })

  const { action: changeEmail, status: changeEmailStatus } = useUpdateEmail({
    setError: emailForm.setError,
  })

  const { action: verifyOtp, status: verifyOtpStatus } = useVerifyEmailOtp({
    setError: otpForm.setError,
  })

  // Timer for OTP expiration (10 minutes = 600 seconds)
  useEffect(() => {
    let interval: NodeJS.Timeout

    if (timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setShowOtpDialog(false)
            toast.error('OTP expired. Please try again.')
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(interval)
  }, [timeRemaining])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleEmailSubmit = async (values: any) => {
    if (values.email === userData.email) {
      emailForm.setError('email', {
        message: 'Please enter a different email address',
      })
      return
    }
    await changeEmail({ email: values.email })
    setPendingEmail(values.email)
    setShowOtpDialog(true)
    setTimeRemaining(600) // 10 minutes
  }

  const handleOtpSubmit = async (values: any) => {
    await verifyOtp({ otp: values.otp, email: pendingEmail })

    setShowOtpDialog(false)
    emailForm.reset()
    otpForm.reset()
    setTimeRemaining(0)
    setPendingEmail('')
  }

  const handleCancelOtp = () => {
    setShowOtpDialog(false)
    otpForm.reset()
    setTimeRemaining(0)
    setPendingEmail('')
    toast.info('Email change cancelled')
  }

  return (
    <div className="space-y-6">
      {status === 'pending' && (
        <div className="flex items-center justify-center">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}

      <div>
        <h2 className="mb-2">Email Address</h2>
        <p className="mb-4 text-gray-600">
          Change your email address with secure verification.
        </p>
      </div>

      <form
        onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
        className="space-y-6"
      >
        <Field>
          <Label htmlFor="currentEmail">Current Email</Label>
          <div className="flex items-center gap-2 rounded-md border bg-gray-50 px-3 py-2">
            <Mail className="h-4 w-4 text-gray-500" />
            {userData.email}
          </div>
        </Field>

        <Field
          statusText={emailForm.formState.errors?.email?.message?.toString()}
          status={emailForm.formState.errors?.email ? 'error' : null}
        >
          <Label htmlFor="email">New Email Address</Label>
          <Input
            id="email"
            type="email"
            {...emailForm.register('email')}
            placeholder="Enter your new email address"
          />
          <FieldStatusMessage />
        </Field>

        <Button type="submit" disabled={changeEmailStatus.status === 'pending'}>
          {changeEmailStatus.status === 'pending'
            ? 'Sending OTP...'
            : 'Change Email'}
        </Button>
      </form>

      {/* OTP Verification Dialog */}
      <Dialog open={showOtpDialog} onOpenChange={setShowOtpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Verify New Email
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
              className="space-y-4"
            >
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
                <p className="mb-2 text-sm text-gray-600">
                  We've sent a 6-digit verification code to:
                </p>
                <p>{pendingEmail}</p>
              </div>

              <Field
                statusText={otpForm.formState.errors?.otp?.message?.toString()}
                status={otpForm.formState.errors?.otp ? 'error' : null}
              >
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  {...otpForm.register('otp')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    otpForm.setValue('otp', value)
                  }}
                  placeholder="Enter 6-digit code"
                  className="text-center text-lg tracking-widest"
                  maxLength={6}
                />
                <FieldStatusMessage />
              </Field>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>Time remaining: {formatTime(timeRemaining)}</span>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={verifyOtpStatus.status === 'pending'}
                  className="flex-1"
                >
                  {verifyOtpStatus.status === 'pending' ? (
                    'Verifying...'
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Verify
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelOtp}
                  disabled={verifyOtpStatus.status === 'pending'}
                  className="flex-1"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>

              <div className="text-center text-xs text-gray-500">
                The verification code will expire in 10 minutes.
              </div>
            </form>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}
